import React from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import { Text } from 'components';
import { useI18n } from 'components/I18n';
import { ClickTracker } from 'components/Tracking';
import { SettingsIcon } from 'components/Icons';
import { MENU_ICON_SIZE } from './navigationUtils';
import Search from './Search';
import { MenuLink, StyledPersistentNavigation, SignUpButton, StyledSettingsLink } from './Styles';

function PersistentNavigation({ shouldShowSignUp, persistentNavItems }) {
  const i18n = useI18n();

  const {
    isLoggedIn,
    isAnonymousUser,
    settingsMenu,
    location,
    loginEnabled,
    hideItemsForUnentitled,
    horizontalMenuHidden,
  } = useSelector(state => ({
    isLoggedIn: state.auth.isLoggedIn,
    isAnonymousUser: state.auth.isAnonymousUser,
    settingsMenu: state.settings.settingsMenu,
    location: state.router.location,
    loginEnabled: state.settings.loginEnabled,
    hideItemsForUnentitled: state.auth.isLoggedIn
      && state.settings.features.payment?.onboarding?.mandatory
      && !state.packs.entitled?.length,
    horizontalMenuHidden: state.ui.horizontalMenuHidden,
  }), shallowEqual);

  const searchItem = persistentNavItems?.find(item => item.type === 'search');
  const settingsItem = persistentNavItems?.find(item => item.type === 'settings');

  const shouldShowSearch = !hideItemsForUnentitled && !!searchItem;
  const shouldShowSettings = !!settingsItem && isLoggedIn && !isAnonymousUser;
  const shouldShowLogout = !settingsItem && !settingsMenu && isLoggedIn && !isAnonymousUser;
  const shouldShowLogin = loginEnabled && (!isLoggedIn || isAnonymousUser);

  if (!shouldShowSearch && !shouldShowSettings && !shouldShowLogout && !shouldShowLogin && !shouldShowSignUp) {
    return null;
  }

  return (
    <StyledPersistentNavigation>
      {shouldShowSearch && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="Search"
        >
          <Search asLink={horizontalMenuHidden ?? false} />
        </ClickTracker>
      )}
      {shouldShowSettings && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="Settings"
        >
          <StyledSettingsLink
            to={{ name: 'settings-account' }}
            data-content={settingsItem?.title || settingsMenu?.title}
            active={location.name.startsWith('settings')}
          >
            <SettingsIcon size={MENU_ICON_SIZE} />
          </StyledSettingsLink>
        </ClickTracker>
      )}
      {shouldShowLogout && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="click_sign_out_button"
        >
          <MenuLink
            to={{ name: 'log-out' }}
            data-content={i18n.formatText('logOut')}
            className="e2e-log-out"
          >
            <Text id="logOut" />
          </MenuLink>
        </ClickTracker>
      )}
      {shouldShowLogin && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="Login"
        >
          <MenuLink
            to={{ name: 'log-in' }}
            className="e2e-log-in"
            data-content={i18n.formatText('logIn')}
            active={location.name === 'log-in'}
          >
            <Text id="logIn" />
          </MenuLink>
        </ClickTracker>
      )}
      {shouldShowSignUp && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="SignUp"
        >
          <SignUpButton
            to={{ name: 'create-account' }}
            data-content={i18n.formatText('menu.signUp')}
            active={location.name === 'create-account'}
          >
            <Text id="menu.signUp" />
          </SignUpButton>
        </ClickTracker>
      )}
    </StyledPersistentNavigation>
  );
}

PersistentNavigation.propTypes = {
  shouldShowSignUp: PropTypes.bool,
  persistentNavItems: PropTypes.array,
  hideItemsForUnentitled: PropTypes.bool,
};

export default React.memo(PersistentNavigation);
